import { useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const phoneAlt = (formData.get("phone_alt") as string || "").trim();
    if (phoneAlt) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setLoading(true);
    setFormError("");

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((val, key) => {
        if (key !== "phone_alt" || !(val as string).trim()) {
          urlEncoded.append(key, val as string);
        }
      });

      const response = await fetch("https://readdy.ai/api/form/d9njm8obdkpk99j7gkqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else if (typeof serverMsg === "string" && serverMsg.includes("spam")) {
        setFormError("发送失败，请稍后重试");
        setFormStatus("error");
      } else {
        setFormError(typeof serverMsg === "string" ? serverMsg : "发送失败，请稍后重试");
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于蓝莓影院 - 品牌介绍、用户口碑与联系方式",
    description: "蓝莓影院品牌介绍、用户口碑与联系方式。致力于提供高清免费在线观影体验，海量影视资源每日更新。",
    url: window.location.href,
    isPartOf: { "@id": `${window.location.origin}/#website` },
    about: {
      "@type": "Organization",
      name: "蓝莓影院",
      description: "蓝莓影院提供高清免费在线观影体验，海量免费电视剧全集、最新电影大全，不缓冲流畅播放，每日更新热门影视资源。",
      url: window.location.origin,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "400-888-0000",
        contactType: "customer service",
        email: "contact@blueberrycinema.com",
        availableLanguage: "Chinese",
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "蓝莓影院是否需要付费观看？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "蓝莓影院所有影片完全免费观看，无需注册会员，无需付费订阅，即点即看，高清流畅播放。我们坚持免费理念，让每一位影迷都能无障碍地享受优质影视内容。",
        },
      },
      {
        "@type": "Question",
        name: "蓝莓影院的影片更新频率如何？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "蓝莓影院每日更新热门影视资源，包括最新上映的电影、热播电视剧、最新动漫和火爆综艺节目，确保用户能够第一时间观看到最新内容。专业的内容运营团队全天候跟踪最新资源。",
        },
      },
      {
        "@type": "Question",
        name: "蓝莓影院支持哪些设备观看？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "蓝莓影院支持电脑、平板、手机等多种设备在线观看。只需打开浏览器访问蓝莓影院网站，即可随时随地享受高清观影体验，无需下载任何APP。网站采用响应式设计，自动适配不同屏幕尺寸。",
        },
      },
      {
        "@type": "Question",
        name: "蓝莓影院的视频画质如何？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "蓝莓影院提供高清画质播放，采用先进的视频编码技术，确保画面清晰流畅。同时采用智能缓冲技术，有效减少卡顿现象，提供最佳观影体验。支持多种清晰度自适应切换。",
        },
      },
      {
        "@type": "Question",
        name: "蓝莓影院有哪些影视分类？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "蓝莓影院涵盖电影、电视剧、动漫、综艺四大核心分类。电影频道包括科幻、喜剧、动作、奇幻、悬疑等类型；电视剧频道涵盖都市、古装、悬疑、爱情等题材；动漫频道涵盖热血、战斗、奇幻、治愈等品类；综艺频道包含竞演、生活、户外、音乐等类型。",
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Breadcrumb items={[{ label: "关于我们" }]} />

      {/* 品牌介绍 Hero */}
      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20home%20theater%20interior%20with%20cozy%20seating%20large%20projection%20screen%20showing%20colorful%20abstract%20movie%20visuals%20soft%20ambient%20lighting%20warm%20golden%20and%20amber%20tones%20inviting%20atmosphere%20rich%20textures%20modern%20minimalist%20design%20no%20people&width=1600&height=600&seq=about-hero-cinema&orientation=landscape"
          alt="蓝莓影院品牌形象 - 温暖舒适的观影环境"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading mb-3">
              关于蓝莓影院
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto">
              致力于为每一位影迷提供最优质的免费观影体验
            </p>
          </div>
        </div>
      </section>

      {/* 品牌介绍内容 */}
      <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-4">
              品牌介绍
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              蓝莓影院成立于2024年，是一家专注于提供<strong>高清免费在线观影服务</strong>的影视平台。我们致力于打破传统观影门槛，让每一位用户都能享受到高品质的影视内容，无需付费订阅，即点即看。
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              平台汇聚了海量的影视资源，涵盖<strong>最新电影</strong>、<strong>热播电视剧</strong>、<strong>人气动漫</strong>、<strong>火爆综艺</strong>等多个品类。从国内热门大片到海外经典佳作，从热门连载动漫到全民追捧的综艺节目，蓝莓影院应有尽有。无论是追求视觉震撼的科幻巨制，还是温暖治愈的日常番剧，都能在这里找到。
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              我们的技术团队采用先进的视频编码和<strong>智能缓冲技术</strong>，确保影片<strong>高清流畅播放</strong>，让用户告别卡顿和等待。同时，我们建立了专业的内容运营团队，每日更新热门影视资源，保证用户能够第一时间观看到最新上映的精彩内容。蓝莓影院以用户体验为核心，持续优化播放技术和内容质量，为千万影迷打造极致的在线观影平台。
            </p>
          </section>

          {/* 核心优势 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  icon: "ri-shield-check-line",
                  title: "完全免费",
                  desc: "所有影片免费观看，无需注册会员，无需付费订阅，真正零门槛观影体验。",
                },
                {
                  icon: "ri-speed-up-line",
                  title: "流畅不缓冲",
                  desc: "智能缓冲技术，高清画质播放，告别卡顿等待，享受极速观影体验。",
                },
                {
                  icon: "ri-refresh-line",
                  title: "每日更新",
                  desc: "专业内容团队每日更新，第一时间获取最新电影、热播剧资源。",
                },
                {
                  icon: "ri-device-line",
                  title: "多端支持",
                  desc: "电脑、平板、手机多端适配，随时随地畅享高清影视内容。",
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 p-5 rounded-xl bg-background-100">
                  <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-500 text-lg`}></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 用户口碑 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  name: "影迷小王",
                  rating: 5,
                  comment: "蓝莓影院真的太棒了！画质清晰流畅，更新速度快，而且完全免费，是我最爱的追剧平台！每天下班回来就打开蓝莓影院，已经成为生活习惯。",
                },
                {
                  name: "动漫爱好者",
                  rating: 5,
                  comment: "动漫资源非常全，热门新番更新及时，播放流畅不卡顿，强烈推荐给所有动漫迷！日语原声和国配都能选，太贴心了。",
                },
                {
                  name: "追剧达人",
                  rating: 5,
                  comment: "每天都在蓝莓影院追剧，分类清晰，找片方便，不用注册就能看，体验非常好。新剧上线速度比很多付费平台还快。",
                },
                {
                  name: "电影发烧友",
                  rating: 4,
                  comment: "电影资源很丰富，从经典老片到最新大片都有，画质不错，观影体验很好。特别推荐科幻和动作分类，片源质量很高。",
                },
                {
                  name: "综艺爱好者",
                  rating: 5,
                  comment: "综艺节目更新很快，热门综艺都有，而且不需要付费，比很多平台都良心！每周末必刷，笑到停不下来。",
                },
                {
                  name: "家庭用户",
                  rating: 5,
                  comment: "全家人都用蓝莓影院，老人看电视剧，孩子看动漫，我追综艺，一个平台满足全家人需求。最重要的是完全免费，省钱又省心！",
                },
              ].map((review) => (
                <div key={review.name} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${i < review.rating ? "text-accent-500" : "text-foreground-200"}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "{review.comment}"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 联系我们 + 表单 */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                  <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm">电子邮箱</h3>
                    <p className="text-xs text-foreground-600">contact@blueberrycinema.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                  <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-accent-500 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm">微信公众号</h3>
                    <p className="text-xs text-foreground-600">蓝莓影院</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                  <span className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-secondary-500 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm">客服热线</h3>
                    <p className="text-xs text-foreground-600">400-888-0000</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                  <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm">工作时间</h3>
                    <p className="text-xs text-foreground-600">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                意见反馈
              </h2>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="p-5 rounded-xl bg-background-100 space-y-4"
              >
                <input
                  type="text"
                  name="phone_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />
                <div>
                  <label htmlFor="feedback-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    您的称呼
                  </label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的名字"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <select
                    id="feedback-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                  >
                    <option value="">请选择反馈类型</option>
                    <option value="功能建议">功能建议</option>
                    <option value="影片推荐">影片推荐</option>
                    <option value="播放问题">播放问题</option>
                    <option value="内容反馈">内容反馈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="feedback-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    详细描述
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请详细描述您的意见或建议（限500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">限500字符</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50"
                >
                  {loading ? "提交中..." : "提交反馈"}
                </button>
                {formStatus === "success" && (
                  <p className="text-green-600 text-sm text-center">感谢您的反馈！我们会认真对待每一条建议。</p>
                )}
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </article>
    </>
  );
}